import React from 'react';
import GeneralImageNew from '@gp/components/GeneralImageNew';
import LinkJump from '@gp/components/LinkJump';
import {
  getFormatNumPlus,
  getArraySort,
  queryJumpable,
} from '@gp/utils/common';
import { history } from 'umi';
import { MENU_ID_MAP, SLASH_G_POP } from '@gp/constants/const';
import {
  TABLE_HEAD_MAP,
  SEGMENT_GROSS_REVENUE,
  SEGMENT_NET_PROFIT,
} from '../constants';
import { FIXED_BO_ORDER_MAP } from '../../../constants';

function getTableList(
  data: IStudioOverallFinanceForecastListResultItem,
  years: string[],
) {
  const resultList = [];
  if (Array.isArray(data) && data?.length && years?.length) {
    // 排序start
    let allTotalItem: any = null,
      othersItem: any = null,
      fixedSortItems: any = [], // 固定排序的bo
      fixedSortedItems: any = [], // 固定排序的bo排好序后
      needSortItems: any[] = [];
    for (let i = 0; i < data?.length; i++) {
      const t = data?.[i];
      if (t?.management_team?.toLowerCase() === 'portfolio total') {
        allTotalItem = t;
      } else if (t?.management_team?.toLowerCase() === 'others') {
        othersItem = t;
      } else if (
        FIXED_BO_ORDER_MAP?.includes(t?.management_team?.toLowerCase())
      ) {
        fixedSortItems.push(t);
      } else {
        needSortItems.push(t);
      }
    }
    // 固定排序的bo
    for (let i = 0; i < FIXED_BO_ORDER_MAP?.length; i++) {
      const t = FIXED_BO_ORDER_MAP?.[i];
      for (let j = 0; j < fixedSortItems?.length; j++) {
        const k = fixedSortItems?.[j];
        if (t === k?.management_team?.toLowerCase()) {
          fixedSortedItems.push(k);
          break;
        }
      }
    }
    let sortResultList = getArraySort(
      needSortItems,
      'string',
      'asc',
      'management_team',
    );
    if (fixedSortedItems?.length) {
      sortResultList = [...fixedSortedItems, ...sortResultList];
    }
    if (allTotalItem) {
      sortResultList?.unshift(allTotalItem);
    }
    if (othersItem) {
      sortResultList?.push(othersItem);
    }
    // 排序end
    if (Array.isArray(sortResultList)) {
      for (let i = 0; i < sortResultList?.length; i++) {
        const t = sortResultList?.[i];
        const studioMetrics = t?.studio_metrics; // studio_metrics后端排序
        if (Array.isArray(studioMetrics)) {
          for (let j = 0; j < studioMetrics?.length; j++) {
            const k = studioMetrics?.[j];
            const isLast = j === studioMetrics?.length - 1;
            const yearsObj: { [keyName: string]: string } = {}; // 所有年份的对应值
            for (let m = 0; m < years?.length; m++) {
              const n = years?.[m];
              const baseCurrentYearItem = Array.isArray(k?.base)
                ? k?.base?.find(
                    (item: { year: string; value: string }) => item?.year === n,
                  ) || ''
                : '';
              const conservativeCurrentYearItem = Array.isArray(k?.conservative)
                ? k?.conservative?.find(
                    (item: { year: string; value: string }) => item?.year === n,
                  ) || ''
                : '';
              yearsObj[`${n}_base`] = baseCurrentYearItem?.value;
              yearsObj[`${n}_conservative`] =
                conservativeCurrentYearItem?.value;
            }
            resultList.push({
              uniqueId: k?.studio?.id + j,
              boName: t?.management_team,
              isSpacerBlock: isLast,
              isAllTotal: k?.studio?.name?.toLowerCase() === 'all studios',
              isTotal:
                k?.studio?.name?.toLowerCase() === 'sub-total' ||
                k?.studio?.name?.toLowerCase() === 'all studios',
              studioId: k?.studio?.id,
              studioName: k?.studio?.name,
              studioCover: k?.studio?.cover,
              ...yearsObj,
            });
          }
        }
      }
    }
  }

  return resultList;
}

const toDetail = (
  studioId: string,
  studioName: string,
  isJump: boolean = true,
) => {
  if (!studioId) return '';
  const filter = encodeURIComponent(
    JSON.stringify({
      studioId,
      studioName,
      menuFrom: MENU_ID_MAP['studio-dashboard_studio-overall'], // 用于标识左侧菜单栏当前选中高亮
      tab: 'finance',
    }),
  );
  const url = SLASH_G_POP + `/Studio/PortfolioOverview/Detail?filter=${filter}`;
  if (isJump) {
    history.push(url);
  }

  return url;
};

// 获取列配置
const getColumns = (segmentValue: '1' | '2', years: string[]) => {
  // 年份列配置
  const yearsOptions = [];
  if (Array.isArray(years) && years?.length) {
    for (let i = 0; i < years?.length; i++) {
      const t = years?.[i];
      yearsOptions.push({
        key: t + '_year',
        header: t + '($)',
        align: 'right',
        width: 130,
        render: (cvm: any) => {
          const v1 = getFormatNumPlus(cvm?.[t + '_base']) || '-',
            v2 = getFormatNumPlus(cvm?.[t + '_conservative']) || '-';

          return (
            <div className="dbg-table-line-box">
              <div className="dbg-table-line-text" title={v1}>
                {v1}
              </div>
              <div className="dbg-table-line-text" title={v2}>
                {v2}
              </div>
            </div>
          );
        },
      });
    }
  }

  return [
    // bo
    {
      key: TABLE_HEAD_MAP?.[segmentValue]?.['bo']?.keyName,
      header: TABLE_HEAD_MAP?.[segmentValue]?.['bo']?.title,
      align: TABLE_HEAD_MAP?.[segmentValue]?.['bo']?.align,
      width: TABLE_HEAD_MAP?.[segmentValue]?.['bo']?.width,
      // fixed: 'left', //和动态计算合并单元格冲突，不能使用
      render: (cvm: any) => {
        return (
          <>
            <div className="dbg-table-bold">
              {cvm?.isTotal ? cvm?.boName || '-' : ''}
            </div>
          </>
        );
      },
    },
    // studio
    {
      key: TABLE_HEAD_MAP?.[segmentValue]?.['studio']?.keyName,
      header: TABLE_HEAD_MAP?.[segmentValue]?.['studio']?.title,
      align: TABLE_HEAD_MAP?.[segmentValue]?.['studio']?.align,
      width: TABLE_HEAD_MAP?.[segmentValue]?.['studio']?.width,
      render: (cvm: any) => {
        return (
          <>
            <div className="dbg-table-studio-box">
              {!cvm?.isTotal && (
                <LinkJump url={toDetail(cvm?.studioId, cvm?.studioName, false)}>
                  <GeneralImageNew
                    className="gp-cursor-pointer"
                    domain="studio"
                    // style={{
                    //   border: '1px solid rgba(0, 0, 0, 0.1)',
                    //   marginRight: 10,
                    // }}
                    // width={24}
                    // height={24}
                    src={cvm?.studioCover}
                    onClick={(e: React.MouseEvent<Element, MouseEvent>) => {
                      if (!queryJumpable(e)) return;
                      toDetail(cvm?.studioId, cvm?.studioName);
                    }}
                  />
                </LinkJump>
              )}
              <LinkJump url={toDetail(cvm?.studioId, cvm?.studioName, false)}>
                <span
                  className={`dbg-table-studio-text ${
                    cvm?.isTotal
                      ? ''
                      : 'dbg-table-studio-name gp-cursor-pointer'
                  }`}
                  title={cvm?.studioName || '-'}
                  onClick={(e) => {
                    if (!queryJumpable(e)) return;
                    toDetail(cvm?.studioId, cvm?.studioName);
                  }}
                >
                  {cvm?.studioName || '-'}
                </span>
              </LinkJump>
            </div>
          </>
        );
      },
    },
    // scenario
    {
      key: TABLE_HEAD_MAP?.[segmentValue]?.['scenario']?.keyName,
      header: TABLE_HEAD_MAP?.[segmentValue]?.['scenario']?.title,
      align: TABLE_HEAD_MAP?.[segmentValue]?.['scenario']?.align,
      width: TABLE_HEAD_MAP?.[segmentValue]?.['scenario']?.width,
      render: (cvm: any) => {
        return (
          <div className="dbg-table-line-box">
            <div className="dbg-table-line-text" title="Base">
              Base
            </div>
            <div className="dbg-table-line-text" title="Conservative">
              Conservative
            </div>
          </div>
        );
      },
    },
    ...yearsOptions,
  ];
};

export { getTableList, getColumns };
